import {
  PEOPLE_SEARCH_MIN_LENGTH,
  isSearchableQuery,
  matchesPersonQuery,
  type PeoplePerson,
} from "@playbookai/types";
import type {
  PeopleFieldItem,
  PeopleFieldModel,
  PeopleFieldModelInput,
  PeopleFieldSection,
  PeopleFieldStatus,
} from "./people-field-model.types.js";

export type * from "./people-field-model.types.js";

/* The open list as data: which sections show, which rows they hold, what
   each row says, and which state the workspace search is in. Pure, so every
   grant mode, the share mode and the typed row are tested without a DOM. */

const LOAD_MORE: PeopleFieldItem = { kind: "loadMore", key: "load-more" };

const normalized = (text: string): string =>
  text.normalize("NFKC").trim().toLocaleLowerCase("en-US");

/** What picking someone without access does, as the row's second line. */
function outsiderLine(
  input: PeopleFieldModelInput,
): Pick<Extract<PeopleFieldItem, { kind: "person" }>, "sub" | "disabled"> {
  const { grantMode, labels } = input;
  if (grantMode.kind === "greyed") {
    return {
      sub: { text: labels.greyed(grantMode.whoCanAddLabel), tone: "locked" },
      disabled: true,
    };
  }
  if (grantMode.kind === "none") return { sub: null, disabled: false };
  const role = grantMode.grantRoleLabel;
  const text =
    input.mode === "share"
      ? labels.shareGrant(role)
      : input.grantTiming === "save"
        ? labels.grantSave(role)
        : input.grantTiming === "send"
          ? labels.grantSend(role)
          : labels.grantNow(role);
  return { sub: { text, tone: "grant" }, disabled: false };
}

export function buildPeopleFieldModel(
  input: PeopleFieldModelInput,
): PeopleFieldModel {
  const { grantMode, labels, search } = input;
  const query = input.query.trim();
  const typing = query.length > 0;
  const searchable = isSearchableQuery(input.query);
  const share = input.mode === "share";
  // A place with an access list names itself ("this VSM"); `none` has neither.
  const noun = grantMode.kind === "none" ? null : grantMode.nounLabel;
  const selectedId = input.value?.kind === "person" ? input.value.id : null;
  const rosterIds = new Set(input.roster.map((person) => person.id));
  const sections: PeopleFieldSection[] = [];

  const row = (person: PeoplePerson, withAccess: boolean): PeopleFieldItem => ({
    kind: "person",
    key: person.id,
    person,
    badge: withAccess ? (person.access ?? null) : null,
    ...(noun === null
      ? { sub: null, disabled: false }
      : withAccess
        ? share
          ? {
              sub: { text: labels.alreadyHasAccess, tone: "muted" as const },
              disabled: true,
            }
          : { sub: null, disabled: false }
        : outsiderLine(input)),
    selected: person.id === selectedId,
    isCurrentUser: person.id === input.currentUserId,
  });

  // "Assign to me" before typing, unless I am already the value.
  const me =
    !typing && !share && input.me && input.me.id !== selectedId
      ? input.me
      : null;
  if (me) {
    sections.push({
      key: "me",
      title: null,
      count: null,
      items: [
        {
          kind: "me",
          key: "me",
          person: me,
          detail: me.access ? `${me.name} · ${me.access.label}` : me.name,
        },
      ],
    });
  }

  if (noun !== null) {
    const local = input.roster.filter(
      (person) => person.id !== me?.id && matchesPersonQuery(person, query),
    );
    // The server also knows who has access: a match the in-memory roster
    // missed (a long roster) still reads as having access.
    if (searchable) {
      for (const person of search.results) {
        if (person.access && !rosterIds.has(person.id)) local.push(person);
      }
    }
    if (local.length > 0) {
      sections.push({
        key: "local",
        title: typing ? labels.inNoun(noun) : labels.withAccess,
        count: local.length,
        items: local.map((person) => row(person, true)),
      });
    }
  }

  const workspaceTitle =
    noun === null ? labels.workspace : labels.notInNoun(noun);
  let status: PeopleFieldStatus = null;
  if (searchable) {
    const outsiders =
      noun === null
        ? search.results
        : search.results.filter(
            (person) => !person.access && !rosterIds.has(person.id),
          );
    const more =
      search.nextCursor !== null && !search.loading && !search.failed;
    if (outsiders.length > 0 || more) {
      const items = outsiders.map((person) => row(person, false));
      sections.push({
        key: "workspace",
        title: workspaceTitle,
        count: outsiders.length,
        items: more ? [...items, LOAD_MORE] : items,
      });
    }
    const anyone = sections.some(
      (section) =>
        (section.key === "local" || section.key === "workspace") &&
        section.items.some((item) => item.kind === "person"),
    );
    status = search.loading
      ? "loading"
      : search.failed
        ? "error"
        : anyone || more
          ? null
          : "empty";
  }

  if (input.allowTyped && typing) {
    const typed = normalized(query);
    const known = [
      ...input.roster,
      ...(searchable ? search.results : []),
      ...(input.value ? [input.value] : []),
    ].some((entry) => normalized(entry.name) === typed);
    if (!known) {
      sections.push({
        key: "typed",
        title: null,
        count: null,
        items: [{ kind: "typed", key: "typed", text: query }],
      });
    }
  }

  const length = input.query.normalize("NFKC").trim().length;
  const hint =
    length < PEOPLE_SEARCH_MIN_LENGTH
      ? labels.hint(PEOPLE_SEARCH_MIN_LENGTH - length, length > 0)
      : null;

  return {
    sections,
    items: sections.flatMap((section) => section.items),
    status,
    workspaceTitle,
    hint,
  };
}
